"use client";

import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { Search, Share2, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { BookingAttendeeSuggestion } from "@/actions/event-booking";
import { searchBookingAttendees } from "@/actions/event-booking";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import type { Gender } from "@/db/schema/profile";
import type { EligibilityFailureReason } from "@/lib/event-booking";
import { shareMessage } from "@/lib/native/share";
import { cn } from "@/lib/utils";

type AttendeeTagPickerProps = {
  hiiveId: number;
  allowedTierIds: number[];
  allowedGenders?: Gender[];
  disabled?: boolean;
  onSelect: (suggestion: BookingAttendeeSuggestion) => void;
  buttonLabel?: string;
  selectedUserIds?: Set<string>;
  currentUserId?: string;
  occurrenceId?: number;
  className?: string;
  /** The ticket keeps the crowd balanced: only men and women can hold it. */
  balanced?: boolean;
  /** The ticket itself, so a member on a full side shows as unavailable. */
  occurrencePassId?: number;
  /**
   * Sides with no seat left once this basket is counted. The server only
   * knows who has booked; this adds the seats already picked here.
   */
  fullSides?: ReadonlyArray<"male" | "female">;
  /**
   * The event to send someone who isn't on Hiive yet. Without it the drawer
   * has no invite button.
   */
  invite?: { eventName: string; eventPath: string };
  /**
   * Swallow the open and do something else instead. Used on the booking page
   * while signed out, where the search action refuses server-side and would
   * otherwise render as "no results".
   */
  interceptOpen?: () => boolean;
};

const REASON_COPY: Record<EligibilityFailureReason, string> = {
  not_member: "Not eligible for this event",
  tier_mismatch: "Not eligible for this ticket",
  gender_mismatch: "Not eligible for this event",
  already_booked: "Already going on this date",
  not_balanced_side: "This ticket is for men and women only",
  missing_contact: "No email on file. Add them as a guest.",
  women_full: "Women's spots are full right now",
  men_full: "Men's spots are full right now",
};

const GROUP_HEADING: Record<BookingAttendeeSuggestion["group"], string> = {
  buddy: "Your buddies",
  booked_with: "You've booked for them before",
  member: "In this hiive",
  other: "On Hiive",
};

/**
 * Pick a Hiive member for a seat. A full-height sheet rather than a popover:
 * it opens on the people the buyer most likely means (their buddies, then
 * people they've booked for before), searches from the first letter, and
 * offers an invite for someone who isn't on Hiive yet.
 */
export function AttendeeTagPicker({
  hiiveId,
  allowedTierIds,
  allowedGenders = [],
  disabled,
  onSelect,
  buttonLabel = "Tag a Hiive member",
  selectedUserIds,
  currentUserId,
  occurrenceId,
  className,
  balanced = false,
  occurrencePassId,
  fullSides = [],
  invite,
  interceptOpen,
}: AttendeeTagPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 150);
  const query = debouncedSearch.trim();

  const tierKey = useMemo(
    () =>
      allowedTierIds.length
        ? allowedTierIds
            .slice()
            .sort((a, b) => a - b)
            .join("-")
        : "none",
    [allowedTierIds],
  );
  const genderKey = useMemo(
    () =>
      allowedGenders.length ? allowedGenders.slice().sort().join("-") : "all",
    [allowedGenders],
  );

  const {
    data: suggestions = [],
    isFetching,
    isPending,
  } = useQuery({
    queryKey: [
      "booking-attendees",
      hiiveId,
      tierKey,
      genderKey,
      occurrenceId ?? "none",
      balanced,
      occurrencePassId ?? "none",
      query,
    ],
    enabled: open && !disabled,
    queryFn: async () => {
      const result = await searchBookingAttendees({
        query,
        hiiveId,
        allowedTierIds,
        allowedGenders,
        occurrenceId,
        balanced,
        occurrencePassId,
      });
      return result.ok ? result.data : [];
    },
    // Keep the last list on screen while the next letter's results load.
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  const groups = useMemo(() => {
    const byGroup = new Map<
      BookingAttendeeSuggestion["group"],
      BookingAttendeeSuggestion[]
    >();
    for (const suggestion of suggestions) {
      const list = byGroup.get(suggestion.group) ?? [];
      list.push(suggestion);
      byGroup.set(suggestion.group, list);
    }
    return [...byGroup.entries()];
  }, [suggestions]);

  const disabledReasonFor = (suggestion: BookingAttendeeSuggestion) => {
    if (selectedUserIds?.has(suggestion.id) && suggestion.id !== currentUserId)
      return "Already on a ticket in this booking";
    if (suggestion.side && fullSides.includes(suggestion.side))
      return REASON_COPY[
        suggestion.side === "female" ? "women_full" : "men_full"
      ];
    return !suggestion.eligible && suggestion.reason
      ? REASON_COPY[suggestion.reason]
      : null;
  };

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  const shareInvite = async () => {
    if (!invite) return;
    const url = `${window.location.origin}${invite.eventPath}`;
    const text = `I'm booking ${invite.eventName} on Hiive. Join so I can add you to my booking:`;
    const outcome = await shareMessage({
      title: invite.eventName,
      text,
      url,
    });
    if (outcome === "copied") toast.success("Invite copied. Paste it to them.");
    if (outcome === "failed") toast.error("Couldn't share right now");
  };

  const showEmpty = !isPending && !isFetching && suggestions.length === 0;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => {
          if (interceptOpen?.()) return;
          setOpen(true);
        }}
        className={cn("gap-2", className)}
      >
        <UserPlus className="size-3.5" aria-hidden="true" />
        {buttonLabel}
      </Button>

      <Drawer
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
      >
        <DrawerContent className="mx-auto flex h-[85dvh] w-full max-w-[540px] flex-col overflow-hidden data-[vaul-drawer-direction=bottom]:rounded-t-[30px]">
          <DrawerHeader className="shrink-0 px-5 pt-3 pb-2 text-left!">
            <DrawerTitle className="text-xl font-bold tracking-tight">
              Who's this ticket for?
            </DrawerTitle>
            <DrawerDescription className="text-sm text-text-secondary">
              Pick someone on Hiive. Not on Hiive? Close this and add their name
              and email instead.
            </DrawerDescription>
          </DrawerHeader>

          <div className="shrink-0 px-5 pb-3">
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-text-secondary"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(changeEvent) => setSearch(changeEvent.target.value)}
                placeholder="Search by name or @username"
                aria-label="Search Hiive members"
                enterKeyHint="search"
                className="h-11 rounded-xl pl-10"
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3">
            {isPending ? (
              <ul aria-hidden="true" className="space-y-1 px-2 pt-6">
                {[0, 1, 2, 3, 4].map((row) => (
                  <li key={row} className="flex items-center gap-3 py-2">
                    <span className="size-10 rounded-full bg-surface-alt" />
                    <span className="h-3.5 w-40 rounded bg-surface-alt" />
                  </li>
                ))}
              </ul>
            ) : null}

            {groups.map(([group, members]) => (
              <section key={group} className="pb-2">
                <h3 className="px-2 pt-3 pb-1.5 text-xs font-semibold tracking-wide text-text-tertiary uppercase">
                  {GROUP_HEADING[group]}
                </h3>
                <ul>
                  {members.map((suggestion) => {
                    const reason = disabledReasonFor(suggestion);
                    return (
                      <li key={suggestion.id}>
                        <button
                          type="button"
                          disabled={Boolean(reason)}
                          onClick={() => {
                            onSelect(suggestion);
                            close();
                          }}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left transition-colors duration-150",
                            reason
                              ? "cursor-not-allowed"
                              : "active:bg-surface-alt pointer-fine:hover:bg-surface-alt",
                          )}
                        >
                          <Avatar
                            className={cn(
                              "size-10 border border-border",
                              reason && "opacity-50",
                            )}
                          >
                            <AvatarImage
                              src={suggestion.avatarUrl ?? undefined}
                            />
                            <AvatarFallback className="text-xs">
                              {initials(suggestion.label)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="min-w-0 flex-1">
                            <span
                              className={cn(
                                "block truncate text-sm font-medium",
                                reason
                                  ? "text-text-tertiary"
                                  : "text-text-primary",
                              )}
                            >
                              {suggestion.label}
                            </span>
                            <span className="block truncate text-xs text-text-tertiary">
                              {reason ??
                                [
                                  suggestion.side === "female"
                                    ? "Woman"
                                    : suggestion.side === "male"
                                      ? "Man"
                                      : null,
                                  suggestion.handle
                                    ? `@${suggestion.handle}`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}

            {showEmpty ? (
              <p className="px-2 pt-8 text-center text-sm text-text-secondary">
                {query
                  ? `No one on Hiive matches "${query}".`
                  : "Search for anyone on Hiive."}
              </p>
            ) : null}
          </div>

          {invite ? (
            <div className="shrink-0 border-t border-border/60 px-5 pt-3 pb-[max(env(safe-area-inset-bottom),16px)]">
              <Button
                type="button"
                variant="outline"
                onClick={() => void shareInvite()}
                className="h-11 w-full gap-2 rounded-full"
              >
                <Share2 className="size-4" aria-hidden="true" />
                Not on Hiive yet? Invite them
              </Button>
            </div>
          ) : (
            <div className="shrink-0 pb-[max(env(safe-area-inset-bottom),16px)]" />
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}

function initials(value: string) {
  return value
    .split(" ", 2)
    .map((segment) => segment[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);
}
